import { Icon } from '@eventbrite/eds-icon';
import { MapPinFill } from '@eventbrite/eds-iconography';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { EVENT_MAP_ID } from '../../../_shared/constants';
import {
    EventDetail,
    EventDetailContent,
    EventDetailHeading,
    EventDetailIcon,
    ICON_COLOR,
    ICON_SIZE,
} from '../EventDetail/EventDetail';

export type LocationProps = {
    hasAddress?: boolean;
    hasMap?: boolean;
    venueMultilineAddress?: string[];
    venueName?: string;
    isOnlineEvent: boolean;
    onlineEventUrl?: string;
};

const smoothScroll = (event: React.SyntheticEvent) => {
    event.preventDefault();

    if (HAS_WINDOW) {
        const href = event.currentTarget.getAttribute('href') as string;
        const target = document.querySelector(href) as HTMLElement;

        const onScroll = () => {
            if (window.pageYOffset.toFixed() === target.offsetTop.toFixed()) {
                window.removeEventListener('scroll', onScroll);
                target.focus();
            }
        };

        window.addEventListener('scroll', onScroll);

        window.scrollTo({
            top: target.offsetTop,
            behavior: 'smooth',
        });
    }
};

export const Location = ({
    isOnlineEvent,
    onlineEventUrl,
    hasAddress,
    hasMap,
    venueMultilineAddress,
    venueName,
}: LocationProps) => {
    if (!hasAddress && !isOnlineEvent) {
        return null;
    }

    const OnlineEvent = () => {
        return (
            <>
                {
                    <p className="eds-text-weight--heavy">
                        {gettext('Online event')}
                    </p>
                }
                {onlineEventUrl && (
                    <>
                        <p className="eds-l-mar-top-1 eds-l-mar-bot-3">
                            {gettext('Already have a ticket?')}
                        </p>
                        <a
                            className="eds-btn eds-btn--button eds-btn--neutral"
                            href={onlineEventUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={smoothScroll}
                        >
                            {gettext('Access the event')}
                        </a>
                    </>
                )}
            </>
        );
    };

    const InPersonEvent = () => {
        return (
            <>
                {venueName && <p>{venueName}</p>}
                {venueMultilineAddress?.map((line, index) => (
                    <p key={index}>{line}</p>
                ))}
                {hasMap && (
                    <p>
                        <a href={`#${EVENT_MAP_ID}`} onClick={smoothScroll}>
                            {gettext('View map')}
                        </a>
                    </p>
                )}
            </>
        );
    };

    const LABEL_ID = 'location-heading';

    return (
        <EventDetail ariaLabelledBy={LABEL_ID}>
            <EventDetailIcon>
                <Icon
                    type={<MapPinFill />}
                    size={ICON_SIZE}
                    color={ICON_COLOR}
                />
            </EventDetailIcon>
            <EventDetailContent>
                <EventDetailHeading>
                    <h3 id={LABEL_ID}>{gettext('Location')}</h3>
                </EventDetailHeading>
                {isOnlineEvent && <OnlineEvent />}
                {hasAddress && <InPersonEvent />}
            </EventDetailContent>
        </EventDetail>
    );
};
